import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { SectionCard, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TPL_STATUS = {
  entwurf: { label: "Entwurf", cls: "bg-muted text-muted-foreground" },
  zur_freigabe: { label: "Zur Freigabe", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
  genehmigt: { label: "Von WhatsApp genehmigt", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  abgelehnt: { label: "Abgelehnt", cls: "bg-red-500/10 text-red-700 dark:text-red-300" },
};

function useWaStatus() {
  return useQuery({ queryKey: ["wa-status"], queryFn: async () => (await api.get("/wa/status")).data });
}

function NotConnectedBanner({ status }) {
  if (status === "connected") return null;
  return (
    <div className="mb-4 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300" data-testid="wa-not-connected-banner">
      WhatsApp ist <strong>nicht verbunden</strong>. Automatiken und Versand sind deaktiviert; es werden keine Nachrichten gesendet. Verbindung im Tab „Einstellungen".
    </div>
  );
}

/* ----------------------------- Übersicht ----------------------------- */
export function CommOverview() {
  const { data: s } = useWaStatus();
  const { data } = useQuery({ queryKey: ["wa-dashboard"], queryFn: async () => (await api.get("/wa/dashboard")).data });
  const m = data || {};
  const cards = [
    { k: "planned", label: "Geplant" }, { k: "sent", label: "Gesendet" },
    { k: "delivered", label: "Zugestellt" }, { k: "failed", label: "Fehlgeschlagen" },
    { k: "answered", label: "Beantwortet" }, { k: "open_negative", label: "Offene negative" },
  ];
  return (
    <div>
      <NotConnectedBanner status={s?.status} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <div key={c.k} className="rounded-lg border bg-card p-4" data-testid={`wa-metric-${c.k}`}>
            <div className="text-2xl font-semibold">{m[c.k] ?? 0}</div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
          </div>
        ))}
      </div>
      <SectionCard title="Bewertungen (Ø & Verteilung)" className="mt-4">
        <div className="mb-3 text-sm">Durchschnitt: <strong>{m.avg_rating ?? 0}</strong> · Abgeschlossen: {m.resolved ?? 0}</div>
        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map((n) => {
            const v = m.distribution?.[String(n)] || 0;
            const max = Math.max(1, ...Object.values(m.distribution || { a: 1 }));
            return (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-4">{n}</span>
                <div className="h-3 flex-1 rounded bg-muted">
                  <div className={`h-3 rounded ${n <= 2 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${(v / max) * 100}%` }} />
                </div>
                <span className="w-6 text-right text-muted-foreground">{v}</span>
              </div>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

/* ----------------------------- Einstellungen ----------------------------- */
export function CommSettings() {
  const { data: s } = useWaStatus();
  const qc = useQueryClient();
  const [f, setF] = useState({ waba_id: "", phone_number_id: "", access_token: "", app_secret: "", default_language: "de_DE" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const connected = s?.status === "connected";

  const save = async () => {
    try { await api.put("/wa/config", f); toast.success("WhatsApp-Verbindung gespeichert"); qc.invalidateQueries({ queryKey: ["wa-status"] }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const disconnect = async () => {
    try { await api.delete("/wa/config"); toast.success("Verbindung getrennt"); qc.invalidateQueries({ queryKey: ["wa-status"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <SectionCard title="WhatsApp Business – Verbindung">
      <Badge className={`mb-3 ${connected ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`} data-testid="wa-settings-status">
        {connected ? "Verbunden" : "Nicht verbunden"}
      </Badge>
      {connected ? (
        <div className="space-y-2 text-sm">
          <div className="text-muted-foreground">WABA: <span className="font-mono">{s?.config?.waba_id}</span></div>
          <div className="text-muted-foreground">Telefonnummer-ID: <span className="font-mono">{s?.config?.phone_number_id}</span></div>
          <Button variant="outline" size="sm" className="mt-2 text-destructive" onClick={disconnect} data-testid="wa-disconnect">Verbindung trennen</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Offizielle Meta WhatsApp Cloud API. Zugangsdaten werden verschlüsselt serverseitig gespeichert und nie im Browser angezeigt.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label className="text-xs">WhatsApp Business Account-ID</Label><Input className="h-9" value={f.waba_id} onChange={(e) => set("waba_id", e.target.value)} data-testid="wa-waba-id" /></div>
            <div className="space-y-1"><Label className="text-xs">Telefonnummer-ID</Label><Input className="h-9" value={f.phone_number_id} onChange={(e) => set("phone_number_id", e.target.value)} data-testid="wa-phone-id" /></div>
            <div className="space-y-1"><Label className="text-xs">Access Token (System User)</Label><Input className="h-9" type="password" value={f.access_token} onChange={(e) => set("access_token", e.target.value)} data-testid="wa-token" /></div>
            <div className="space-y-1"><Label className="text-xs">App Secret</Label><Input className="h-9" type="password" value={f.app_secret} onChange={(e) => set("app_secret", e.target.value)} data-testid="wa-secret" /></div>
          </div>
          <Button onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="wa-config-save" disabled={!f.waba_id || !f.phone_number_id || !f.access_token || !f.app_secret}>Verbindung speichern</Button>
        </div>
      )}
      <div className="mt-4 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
        <p className="font-medium text-foreground">Webhook (in der Meta-App eintragen):</p>
        <code className="block break-all rounded bg-background px-2 py-1">{BACKEND}/api/wa/webhook</code>
        <p>Verify-Token wird serverseitig als Secret gesetzt. Signaturen werden pro Mandant mit dem App Secret geprüft.</p>
      </div>
    </SectionCard>
  );
}

/* ----------------------------- Automatisierungen ----------------------------- */
export function CommAutomations({ templates }) {
  const { data: s } = useWaStatus();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["wa-automations"], queryFn: async () => (await api.get("/wa/automations")).data });
  const [b, setB] = useState(null);
  const [sat, setSat] = useState(null);
  const bd = b ?? data?.birthday ?? {};
  const st = sat ?? data?.satisfaction ?? {};
  const approved = (templates || []).filter((t) => t.status === "genehmigt");

  const saveBirthday = async () => {
    try { await api.put("/wa/automations/birthday", bd); toast.success("Geburtstags-Automatik gespeichert"); qc.invalidateQueries({ queryKey: ["wa-automations"] }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const saveSat = async () => {
    try { await api.put("/wa/automations/satisfaction", st); toast.success("Zufriedenheits-Automatik gespeichert"); qc.invalidateQueries({ queryKey: ["wa-automations"] }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const toggleTrigger = (tr) => {
    const cur = new Set(st.triggers || []);
    cur.has(tr) ? cur.delete(tr) : cur.add(tr);
    setSat({ ...st, triggers: [...cur] });
  };

  return (
    <div className="space-y-4">
      <NotConnectedBanner status={s?.status} />
      <SectionCard title="Automatische Geburtstagsnachrichten">
        <div className="space-y-3">
          <div className="flex items-center justify-between"><Label>Aktiviert</Label>
            <Switch checked={!!bd.enabled} onCheckedChange={(v) => setB({ ...bd, enabled: v })} data-testid="wa-birthday-enabled" /></div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1"><Label className="text-xs">Versandzeit</Label><Input className="h-9" type="time" value={bd.send_time || "09:00"} onChange={(e) => setB({ ...bd, send_time: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Zeitzone</Label><Input className="h-9" value={bd.timezone || "Europe/Berlin"} onChange={(e) => setB({ ...bd, timezone: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Vorlage (genehmigt)</Label>
              <Select value={bd.template_id || "none"} onValueChange={(v) => setB({ ...bd, template_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="wa-birthday-template"><SelectValue placeholder="Keine" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Keine</SelectItem>{approved.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <Button size="sm" onClick={saveBirthday} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="wa-birthday-save">Speichern</Button>
        </div>
      </SectionCard>

      <SectionCard title="Kundenzufriedenheit per WhatsApp">
        <div className="space-y-3">
          <div className="flex items-center justify-between"><Label>Aktiviert</Label>
            <Switch checked={!!st.enabled} onCheckedChange={(v) => setSat({ ...st, enabled: v })} data-testid="wa-sat-enabled" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Auslöser</Label>
            <div className="flex flex-wrap gap-3">
              {[["manuell", "Manuell"], ["vertrag", "Nach aktivem/verlängertem Vertrag"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <Switch checked={(st.triggers || []).includes(k)} onCheckedChange={() => toggleTrigger(k)} data-testid={`wa-trigger-${k}`} /> {l}
                </label>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1"><Label className="text-xs">Verzögerung (Std.)</Label><Input className="h-9" type="number" value={st.delay_hours ?? 24} onChange={(e) => setSat({ ...st, delay_hours: Number(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="text-xs">Mindestabstand (Tage)</Label><Input className="h-9" type="number" value={st.min_interval_days ?? 90} onChange={(e) => setSat({ ...st, min_interval_days: Number(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="text-xs">Vorlage (genehmigt)</Label>
              <Select value={st.template_id || "none"} onValueChange={(v) => setSat({ ...st, template_id: v === "none" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="wa-sat-template"><SelectValue placeholder="Keine" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Keine</SelectItem>{approved.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <Button size="sm" onClick={saveSat} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="wa-sat-save">Speichern</Button>
        </div>
      </SectionCard>
    </div>
  );
}

/* ----------------------------- Vorlagen ----------------------------- */
export function CommTemplates({ templates, refetch }) {
  const [edit, setEdit] = useState(null);
  const empty = { name: "", language: "de_DE", category: "UTILITY", purpose: "sonstige", body: "", status: "entwurf", active: true };

  const save = async () => {
    try {
      if (edit.id) await api.put(`/wa/templates/${edit.id}`, edit);
      else await api.post("/wa/templates", edit);
      toast.success("Vorlage gespeichert"); setEdit(null); refetch();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (id) => { try { await api.delete(`/wa/templates/${id}`); refetch(); } catch (err) { toast.error(apiError(err)); } };

  return (
    <SectionCard title="Nachrichtenvorlagen" action={<Button size="sm" onClick={() => setEdit(empty)} data-testid="wa-template-new">Neue Vorlage</Button>}>
      <p className="mb-3 text-xs text-muted-foreground">Nur <strong>von WhatsApp genehmigte</strong> Vorlagen sind produktiv versandbereit. Platzhalter: {"{{vorname}}, {{nachname}}, {{unternehmen}}"}.</p>
      {(templates || []).length === 0 ? <EmptyState title="Keine Vorlagen" /> : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`wa-template-${t.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{t.name}</span>
                  <Badge className={TPL_STATUS[t.status]?.cls}>{TPL_STATUS[t.status]?.label || t.status}</Badge>
                  <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                  <Badge variant="outline" className="text-[10px]">{t.language}</Badge>
                </div>
                <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{t.body}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="outline" onClick={() => setEdit(t)}>Bearbeiten</Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => del(t.id)}>Löschen</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Vorlage bearbeiten" : "Neue Vorlage"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Name (wie in WhatsApp Manager)</Label><Input className="h-9" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="wa-tpl-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Sprache</Label><Input className="h-9" value={edit.language} onChange={(e) => setEdit({ ...edit, language: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Kategorie</Label>
                  <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="UTILITY">Utility</SelectItem><SelectItem value="MARKETING">Marketing</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Zweck</Label>
                  <Select value={edit.purpose} onValueChange={(v) => setEdit({ ...edit, purpose: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="geburtstag">Geburtstag</SelectItem><SelectItem value="zufriedenheit">Zufriedenheit</SelectItem><SelectItem value="werbung">Werbung</SelectItem><SelectItem value="sonstige">Sonstige</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Freigabestatus</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                    <SelectTrigger className="h-9" data-testid="wa-tpl-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TPL_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Text</Label><Textarea rows={6} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} data-testid="wa-tpl-body" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Abbrechen</Button>
            <Button onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="wa-tpl-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
